import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface VideoCarouselProps {
  urls: string[];
  className?: string;
}

export function VideoCarousel({ urls, className }: VideoCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActive(Math.max(0, Math.min(urls.length - 1, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [urls.length]);

  function scrollTo(idx: number) {
    const el = scrollerRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(urls.length - 1, idx));
    el.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
  }

  if (urls.length === 0) {
    return (
      <div
        className={cn(
          'aspect-[9/16] w-full rounded-2xl bg-tg-section-bg flex items-center justify-center text-sm text-tg-hint',
          className,
        )}
      >
        Видео недоступно
      </div>
    );
  }

  const hasMultiple = urls.length > 1;
  const canPrev = active > 0;
  const canNext = active < urls.length - 1;

  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 scrollbar-none"
        >
          {urls.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="snap-center shrink-0 w-full pr-2 last:pr-0"
            >
              <video
                src={url}
                autoPlay={idx === 0}
                muted
                loop
                playsInline
                controls
                preload={idx === 0 ? 'auto' : 'metadata'}
                className="w-full rounded-2xl bg-black aspect-[9/16] object-cover"
              />
            </div>
          ))}
        </div>

        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={() => scrollTo(active - 1)}
              disabled={!canPrev}
              aria-label="Предыдущее видео"
              className={cn(
                'absolute left-1 top-1/2 -translate-y-1/2 z-10',
                'h-9 w-9 rounded-full bg-black/55 text-white backdrop-blur',
                'flex items-center justify-center',
                'transition-opacity hover:bg-black/70',
                'disabled:opacity-0 disabled:pointer-events-none',
              )}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => scrollTo(active + 1)}
              disabled={!canNext}
              aria-label="Следующее видео"
              className={cn(
                'absolute right-1 top-1/2 -translate-y-1/2 z-10',
                'h-9 w-9 rounded-full bg-black/55 text-white backdrop-blur',
                'flex items-center justify-center',
                'transition-opacity hover:bg-black/70',
                'disabled:opacity-0 disabled:pointer-events-none',
              )}
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {hasMultiple && (
        <div className="flex justify-center gap-1.5 mt-2">
          {urls.map((url, i) => (
            <button
              type="button"
              key={`dot-${url}-${i}`}
              onClick={() => scrollTo(i)}
              aria-label={`К видео ${i + 1}`}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === active ? 'w-4 bg-tg-text' : 'w-1.5 bg-tg-hint/50 hover:bg-tg-hint',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
